import Footer from "./common/Footer";
import HeaderDashboard from "./common/HeaderDashboard";
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import Select from "react-select";
import "@google/model-viewer";

import level1Data from "../data/level1.json";
import level2Data from "../data/level2.json";
import level3Data from "../data/level3.json";
import level4Data from "../data/level4.json";
import databaseModelData from "../data/anatomy-unlocked-dev.glbmodels-live-22-10-26.json";
import axios from "axios";

function UploadModel() {
    const { user } = useAuth();
    const navigate = useNavigate();

    /* =======================
       STATE
    ======================= */
    const [selectedLevel1, setSelectedLevel1] = useState(null);
    const [selectedLevel2, setSelectedLevel2] = useState(null);
    const [selectedLevel3, setSelectedLevel3] = useState(null);
    const [selectedLevel4, setSelectedLevel4] = useState(null);

    const [glbFile, setGlbFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const [materialsUI, setMaterialsUI] = useState([]);
    const [materialChanges, setMaterialChanges] = useState({
        added: [],
        removed: [],
    });
    const [errorMessage, setErrorMessage] = useState("");
    const [disabled, setDisabled] = useState(false);

    const modelRef = useRef(null);

    const [dragIndex, setDragIndex] = useState(null);

    const handleDragStart = (index) => {
        setDragIndex(index);
    };

    const handleDragOver = (e) => {
        e.preventDefault(); // REQUIRED
    };

    const handleDrop = (dropIndex) => {
        if (dragIndex === null || dragIndex === dropIndex) return;

        const updated = [...materialsUI];
        const [movedItem] = updated.splice(dragIndex, 1);
        updated.splice(dropIndex, 0, movedItem);

        setMaterialsUI(updated);
        setDragIndex(null);
    };

    /* =======================
       OPTIONS
    ======================= */
    const level1Options = Object.entries(level1Data).map(([key, value]) => ({
        value: key,
        label: value.name,
        slug: value.slug,
    }));

    const level2Options = selectedLevel1
        ? [
            { value: "", label: "Select" },
            ...Object.entries(level2Data)
                .filter(([key]) => key.startsWith(`${selectedLevel1.slug}/`))
                .map(([key, value]) => ({
                    value: key,
                    label: value.name,
                    slug: value.slug,
                })),
        ]
        : [];

    const level3Options = selectedLevel2
        ? [
            { value: "", label: "Select" },
            ...Object.entries(level3Data)
                .filter(([key]) => key.startsWith(`${selectedLevel2.value}/`))
                .map(([key, value]) => ({
                    value: key,
                    label: value.name,
                    slug: value.slug,
                })),
        ]
        : [];

    const level4Options = selectedLevel3
        ? [
            { value: "", label: "Select" },
            ...Object.entries(level4Data)
                .filter(([key]) => key.startsWith(`${selectedLevel3.value}/`))
                .map(([key, value]) => ({
                    value: key,
                    label: value.name,
                    slug: value.slug,
                })),
        ]
        : [];

    /* =======================
       FILE HANDLER
    ======================= */
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setGlbFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setMaterialsUI([]);
        setMaterialChanges({ added: [], removed: [] });
        setErrorMessage("");
    };

    /* =======================
       MODEL LOAD
    ======================= */
    const onLoad = useCallback(() => {
        const modelViewer = modelRef.current;
        if (!modelViewer?.model) return;

        if (!selectedLevel1 || !selectedLevel2 || !selectedLevel3) {
            setErrorMessage("Select Level 1, Level 2, and Level 3 before comparing materials.");
            return;
        }

        const rawMaterials = modelViewer.model.materials;
        const glbMaterials = Array.isArray(rawMaterials)
            ? rawMaterials
            : Array.from(rawMaterials.values());

        if (!glbMaterials.length) {
            setMaterialsUI([]);
            setErrorMessage("No materials were found in the selected GLB.");
            return;
        }

        const level4 = selectedLevel4?.slug || "";
        const existingModel = databaseModelData.find(
            (model) =>
                model.level1 === selectedLevel1.slug &&
                model.level2 === selectedLevel2.slug &&
                model.level3 === selectedLevel3.slug &&
                (model.level4 || "") === level4
        );
        const existingMaterials = existingModel?.materials || [];
        const existingMaterialMap = new Map(
            existingMaterials.map((material) => [material.originalName, material])
        );
        let nextNewMaterialOrder = existingMaterials.reduce(
            (highestOrder, material) => Math.max(highestOrder, Number(material.order) || 0),
            -1
        ) + 1;
        const glbNames = new Set(
            glbMaterials.map((material, index) => material.name || `Material ${index}`)
        );

        const uiData = glbMaterials.map((mat, index) => {
            const originalName = mat.name || `Material ${index}`;
            const existingMaterial = existingMaterialMap.get(originalName);
            const existingOrder = Number(existingMaterial?.order);

            return {
                index,
                originalName,
                displayName: existingMaterial?.displayName ?? originalName,
                order: Number.isFinite(existingOrder)
                    ? existingOrder
                    : nextNewMaterialOrder++,
                materialRef: mat,
                visible: true,
            };
        });

        uiData.sort((a, b) => a.order - b.order || a.index - b.index);
        setMaterialsUI(uiData);
        setMaterialChanges({
            added: uiData
                .filter((material) => !existingMaterialMap.has(material.originalName))
                .map((material) => material.originalName),
            removed: existingMaterials
                .filter((material) => !glbNames.has(material.originalName))
                .map((material) => material.originalName),
        });
        setErrorMessage("");
    }, [selectedLevel1, selectedLevel2, selectedLevel3, selectedLevel4]);
    /* =======================
       MATERIAL CONTROLS
    ======================= */

    const toggleMaterial = (i) => {
        setMaterialsUI((prev) =>
            prev.map((item, idx) => {
                if (idx !== i) return item;

                const mat = item.materialRef;
                if (!mat || !mat.pbrMetallicRoughness) return item;

                const visible = !item.visible;

                // 🔒 Preserve original base color ONCE
                if (!mat._originalBaseColor) {
                    if (mat.pbrMetallicRoughness.baseColorFactor) {
                        mat._originalBaseColor = [
                            ...mat.pbrMetallicRoughness.baseColorFactor,
                        ];
                    } else {
                        mat.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, 1]);
                        mat._originalBaseColor = [1, 1, 1, 1];
                    }
                }

                // 🔁 Match loadMenu behavior
                mat.setAlphaMode("MASK");
                mat.setAlphaCutoff(0.5);

                const [r, g, b, a] = mat._originalBaseColor;

                mat.pbrMetallicRoughness.setBaseColorFactor([
                    r,
                    g,
                    b,
                    visible ? a : 0,
                ]);

                return { ...item, visible };
            })
        );
    };

    const updateDisplayName = (i, value) => {
        setMaterialsUI((prev) =>
            prev.map((item, idx) =>
                idx === i ? { ...item, displayName: value } : item
            )
        );
    };

    const downloadUpdatedDatabaseJson = () => {
        if (!glbFile || !selectedLevel1 || !selectedLevel2 || !selectedLevel3 || !materialsUI.length) {
            setErrorMessage("Select a model, Level 1, Level 2, and Level 3 before exporting.");
            return;
        }

        const level4 = selectedLevel4?.slug || "";
        const modelIndex = databaseModelData.findIndex(
            (model) =>
                model.level1 === selectedLevel1.slug &&
                model.level2 === selectedLevel2.slug &&
                model.level3 === selectedLevel3.slug &&
                (model.level4 || "") === level4
        );
        const oldModel = modelIndex >= 0 ? databaseModelData[modelIndex] : null;
        const oldMaterialsByName = new Map(
            (oldModel?.materials || []).map((material) => [
                material.originalName,
                material,
            ])
        );
        const updatedModel = {
            ...(oldModel || {
                level1: selectedLevel1.slug,
                level2: selectedLevel2.slug,
                level3: selectedLevel3.slug,
                level4,
                level5: "",
            }),
            file_name: glbFile.name,
            materials: materialsUI.map((material, order) => {
                const previousMaterial = oldMaterialsByName.get(material.originalName);
                return {
                    ...(previousMaterial?._id ? { _id: previousMaterial._id } : {}),
                    originalName: material.originalName,
                    displayName: material.displayName,
                    order,
                };
            }),
        };
        const updatedDatabase = [...databaseModelData];

        if (modelIndex >= 0) {
            updatedDatabase[modelIndex] = updatedModel;
        } else {
            updatedDatabase.push(updatedModel);
        }

        const blob = new Blob([JSON.stringify(updatedDatabase, null, 2)], {
            type: "application/json",
        });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = "anatomy-unlocked-dev.glbmodels-live-22-10-26.updated.json";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
    };

    /* =======================
       SUBMIT (ready for API)
    ======================= */
    const handleSubmit = async () => {
        if (!selectedLevel1 || !selectedLevel2 || !selectedLevel3 || !glbFile) {
            setErrorMessage("Select Level 1, Level 2, Level 3, and a GLB file before saving.");
            return;
        }

        try {
            setDisabled(true);

            const materialConfig = materialsUI.map((m, order) => ({
                originalName: m.originalName,
                displayName: m.displayName,
                order,
            }));

            const fileName = glbFile.name;
            const presignResponse = await axios.post(
                `${process.env.REACT_APP_API_URL}/get-model-presign-url`,
                { file_name: fileName }
            );
            const uploadUrl = presignResponse.data;

            if (typeof uploadUrl !== "string" || !uploadUrl) {
                throw new Error("The server did not return a valid model upload URL.");
            }

            await axios.put(uploadUrl, glbFile, {
                headers: { "Content-Type": "model/gltf-binary" },
            });

            const payload = {
                level1: selectedLevel1.slug,
                level2: selectedLevel2.slug,
                level3: selectedLevel3.slug,
                level4: selectedLevel4?.slug || "",
                file_name: fileName,
                glbFile: fileName,
                materials: materialConfig.length ? materialConfig : undefined,
            };

            await axios.post(
                `${process.env.REACT_APP_API_URL}/add-glb-model`,
                payload
            );

            alert("GLB model uploaded successfully!");
            window.location.reload();

        } catch (error) {
            console.error("❌ Upload failed:", error);

            if (error?.response) {
                setErrorMessage(error.response.data?.message || "Server error");
            } else {
                setErrorMessage(error?.message || "Upload failed. Check S3, CORS, or network.");
            }

            setDisabled(false);
        }
    };



    useEffect(() => {
        const modelViewer = modelRef.current;
        if (!modelViewer) return;

        modelViewer.addEventListener("load", onLoad);
        if (modelViewer.model) onLoad();

        return () => {
            modelViewer.removeEventListener("load", onLoad);
        };
    }, [onLoad, previewUrl]);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);
    return (
        <div className="w-100 h-100 bg-green5">
            <HeaderDashboard />

            <main className="main-section mx-4 rounded-top-3 bg-white custom-scroller overflow-auto">
                <div className="container pt-4 pb-5">

                    <h1 className="fw-bold">Upload Model</h1>
                    {errorMessage && (
                        <div className="alert alert-danger mt-3" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    {/* FORM */}
                    <form className="row g-3 mt-3">

                        <div className="col-md-6">
                            <label>Level 1 *</label>
                            <Select
                                options={level1Options}
                                value={selectedLevel1}
                                onChange={(option) => {
                                    setSelectedLevel1(option);
                                    setSelectedLevel2(null);
                                    setSelectedLevel3(null);
                                    setSelectedLevel4(null);
                                    setMaterialsUI([]);
                                    setMaterialChanges({ added: [], removed: [] });
                                    setErrorMessage("");
                                }}
                            />
                        </div>

                        <div className="col-md-6">
                            <label>Level 2 *</label>
                            <Select
                                options={level2Options}
                                value={selectedLevel2}
                                onChange={(o) => {
                                    setSelectedLevel2(!o || o.value === "" ? null : o);
                                    setSelectedLevel3(null);
                                    setSelectedLevel4(null);
                                    setMaterialsUI([]);
                                    setMaterialChanges({ added: [], removed: [] });
                                    setErrorMessage("");
                                }}
                                isDisabled={!selectedLevel1}
                            />
                        </div>


                        <div className="col-md-6">
                            <label>Level 4</label>
                            <Select
                                options={level4Options}
                                value={selectedLevel4}
                                onChange={setSelectedLevel4}
                                isDisabled={!selectedLevel3}
                            />
                        </div>

                        <div className="col-md-6">
                            <label>Level 3</label>
                            <Select
                                options={level3Options}
                                value={selectedLevel3}
                                onChange={(o) => {
                                    setSelectedLevel3(!o || o.value === "" ? null : o);
                                    setSelectedLevel4(null);
                                    setMaterialsUI([]);
                                    setMaterialChanges({ added: [], removed: [] });
                                    setErrorMessage("");
                                }}
                                isDisabled={!selectedLevel2}
                            />
                        </div>


                        <div className="col-md-6">
                            <label>Browse GLB *</label>
                            <input
                                type="file"
                                className="form-control"
                                accept=".glb"
                                onChange={handleFileChange}
                            />
                        </div>

                    </form>

                    {/* PREVIEW + MATERIAL PANEL */}
                    {previewUrl && (
                        <div className="mt-4 d-flex gap-4 col-md-12">

                            {/* MODEL */}
                            <model-viewer
                                ref={modelRef}
                                src={previewUrl}
                                camera-controls
                                environment-image="/images/hdri/neutral.hdr"
                                interaction-prompt="none"
                                style={{ width: "600px", height: "500px", background: "#f5f5f5" }}
                                className="col-md-6"
                            />

                            {/* MATERIAL PANEL */}
                            <div className="col-md-6">
                                <h5 className="fw-bold mb-2">Materials</h5>

                                <div className="border rounded p-2" style={{ maxHeight: "450px", overflowY: "auto" }}>
                                    {materialsUI.map((item, i) => (
                                        <div
                                            key={item.originalName + i}
                                            draggable
                                            onDragStart={() => handleDragStart(i)}
                                            onDragOver={handleDragOver}
                                            onDrop={() => handleDrop(i)}
                                            className="d-flex align-items-center gap-4 mb-2 p-2 border rounded bg-white"
                                            style={{ cursor: "grab" }}
                                        >
                                            {/* Hide / Show */}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => toggleMaterial(i)}
                                                title={item.visible ? "Hide" : "Show"}
                                            >
                                                {item.visible ? "👁" : "🚫"}
                                            </button>
                                            {/* Drag handle */}
                                            <span
                                                className="text-muted"
                                                style={{ cursor: "grab", fontSize: "18px" }}
                                                title="Drag to reorder"
                                            >
                                                ☰
                                            </span>

                                            {/* Order number */}
                                            <span className="badge bg-secondary">{i + 1}</span>

                                            {/* Original name */}
                                            <span
                                                className="text-muted small"
                                                style={{ minWidth: "200px" }}
                                            >
                                                {item.originalName}
                                            </span>

                                            {/* Editable display name */}
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={item.displayName}
                                                onChange={(e) =>
                                                    updateDisplayName(i, e.target.value)
                                                }
                                                placeholder="Portal name"
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-3 small">
                                    <div>New materials: {materialChanges.added.length}</div>
                                    {materialChanges.added.length > 0 && (
                                        <div>{materialChanges.added.join(", ")}</div>
                                    )}
                                    <div className="mt-2">Removed materials: {materialChanges.removed.length}</div>
                                    {materialChanges.removed.length > 0 && (
                                        <div>{materialChanges.removed.join(", ")}</div>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-outline-primary w-100 mt-3"
                                    onClick={downloadUpdatedDatabaseJson}
                                >
                                    Download Updated Database JSON
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary w-100 mt-3"
                                    onClick={handleSubmit}
                                    disabled={disabled}
                                >
                                    {disabled ? "Uploading..." : "Upload Model and Save Config"}
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </main>

            <Footer />
        </div>
    );
}

export default UploadModel;
